import * as React from "react";

import { Input } from "@/components/ui/input";

type DecimalInputProps = Omit<React.ComponentProps<"input">, "onChange" | "value" | "type" | "inputMode"> & {
  value: number | undefined;
  onValueChange: (value: number | undefined) => void;
};

const PARTIAL_DECIMAL = /^\d*[.,]?\d*$/;

const toDisplay = (value: number | undefined): string => (value === undefined ? "" : String(value).replace(".", ","));

/** Accepts both "," and "." as decimal separator, regardless of browser locale. */
export const DecimalInput = ({ value, onValueChange, onFocus, onBlur, ...inputProps }: DecimalInputProps) => {
  const [raw, setRaw] = React.useState(() => toDisplay(value));
  const [isEditing, setIsEditing] = React.useState(false);

  React.useEffect(() => {
    if (!isEditing) setRaw(toDisplay(value));
  }, [value, isEditing]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (!PARTIAL_DECIMAL.test(next)) return;
    setRaw(next);
    const normalized = next.replace(",", ".");
    onValueChange(normalized === "" || normalized === "." ? undefined : Number(normalized));
  };

  return (
    <Input
      {...inputProps}
      type="text"
      inputMode="decimal"
      value={raw}
      onChange={handleChange}
      onFocus={(event) => {
        setIsEditing(true);
        onFocus?.(event);
      }}
      onBlur={(event) => {
        setIsEditing(false);
        onBlur?.(event);
      }}
    />
  );
};
